/**
 * Forms compactos para todos os eventos eSocial NÃO-PERIÓDICOS + CADASTROS INICIAIS.
 *
 * Padrão de cada form:
 *   - Recebe `state`, `setState` (controle externo)
 *   - Não envia — quem submete é o pai (`EsocialPage.js`)
 *   - Retorna apenas os Inputs/Selects + helper de validação
 */
import React from 'react';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Textarea } from '../../ui/textarea';
import {
  MOTIVOS_DESLIGAMENTO,
} from './esocialCodigos';
import { useCodigos } from './useEsocialCodigos';
import EmpresaFuncionarioSelect from './EmpresaFuncionarioSelect';

/**
 * Sel — Dropdown padrão dos forms eSocial.
 * Preferir passar `categoria` (busca da tabela paramétrica `esocial_codigos`).
 * `items` continua aceito como fallback para listas locais (ex.: Sim/Não).
 */
const Sel = ({ value, onValueChange, items, categoria, placeholder, testid }) => {
  const { items: fetched, loading } = useCodigos(categoria || '__none__');
  const data = categoria ? fetched : (items || []);
  return (
    <Select value={value || ''} onValueChange={onValueChange}>
      <SelectTrigger data-testid={testid}>
        <SelectValue placeholder={loading && categoria ? 'Carregando...' : (placeholder || 'Selecione...')} />
      </SelectTrigger>
      <SelectContent className="max-h-72">
        {data.map(i => <SelectItem key={i.codigo} value={i.codigo}>{i.codigo} — {i.descricao}</SelectItem>)}
      </SelectContent>
    </Select>
  );
};

const Fld = ({ label, children, full, hint }) => (
  <div className={full ? 'col-span-2' : ''}>
    <Label>{label}</Label>
    {children}
    {hint && <p className="text-[11px] text-slate-500 mt-1">{hint}</p>}
  </div>
);

// ============================================================================
// S-2200 — Admissão
// ============================================================================
export function S2200Form({ state, setState }) {
  return (
    <>
      <EmpresaFuncionarioSelect
        companyId={state.company_id} employeeId={state.employee_id}
        regime="CLT"
        onChange={p => setState({ ...state, ...p })}
      />
      <Fld label="Data de Admissão *">
        <Input type="date" value={state.dt_admissao || ''} onChange={e => setState({ ...state, dt_admissao: e.target.value })}
               data-testid="esocial-s2200-dt-admissao" />
      </Fld>
      <Fld label="Categoria do Trabalhador *">
        <Sel value={state.cod_categ} onValueChange={v => setState({ ...state, cod_categ: v })}
             categoria="CATEGORIAS_TRABALHADOR" testid="esocial-s2200-categ" />
      </Fld>
      <Fld label="Salário Base (R$) *">
        <Input type="number" step="0.01" min="0" value={state.vr_sal_fx || ''} onChange={e => setState({ ...state, vr_sal_fx: e.target.value })}
               placeholder="2500.00" data-testid="esocial-s2200-salario" />
      </Fld>
      <Fld label="CBO do Cargo *" hint="6 dígitos — ex.: 252210 (Analista)">
        <Input value={state.cbo_cargo || ''} onChange={e => setState({ ...state, cbo_cargo: e.target.value })}
               placeholder="252210" data-testid="esocial-s2200-cbo" />
      </Fld>
      <Fld label="Raça/Cor">
        <Sel value={state.raca_cor} onValueChange={v => setState({ ...state, raca_cor: v })} categoria="RACA_COR" />
      </Fld>
      <Fld label="Estado Civil">
        <Sel value={state.est_civ} onValueChange={v => setState({ ...state, est_civ: v })} categoria="ESTADO_CIVIL" />
      </Fld>
      <Fld label="Grau de Instrução">
        <Sel value={state.grau_instr} onValueChange={v => setState({ ...state, grau_instr: v })} categoria="GRAU_INSTRUCAO" />
      </Fld>
      <Fld label="Sexo">
        <Sel value={state.sexo} onValueChange={v => setState({ ...state, sexo: v })} categoria="SEXO" />
      </Fld>
    </>
  );
}

// ============================================================================
// S-2205 — Alteração Cadastral
// ============================================================================
export function S2205Form({ state, setState }) {
  return (
    <>
      <EmpresaFuncionarioSelect
        companyId={state.company_id} employeeId={state.employee_id}
        onChange={p => setState({ ...state, ...p })}
      />
      <Fld label="CPF do Trabalhador *">
        <Input value={state.cpf_trab || ''} onChange={e => setState({ ...state, cpf_trab: e.target.value.replace(/\D/g, '') })}
               placeholder="12345678900" data-testid="esocial-s2205-cpf" />
      </Fld>
      <Fld label="Data da Alteração *">
        <Input type="date" value={state.dt_alteracao || ''}
               onChange={e => setState({ ...state, dt_alteracao: e.target.value })} />
      </Fld>
      <Fld label="Novo Nome Completo *">
        <Input value={state.nm_trab || ''} onChange={e => setState({ ...state, nm_trab: e.target.value })} />
      </Fld>
      <Fld label="Data de Nascimento *">
        <Input type="date" value={state.dt_nascto || ''}
               onChange={e => setState({ ...state, dt_nascto: e.target.value })} />
      </Fld>
      <Fld label="Sexo">
        <Sel value={state.sexo} onValueChange={v => setState({ ...state, sexo: v })} categoria="SEXO" />
      </Fld>
      <Fld label="Estado Civil">
        <Sel value={state.est_civ} onValueChange={v => setState({ ...state, est_civ: v })} categoria="ESTADO_CIVIL" />
      </Fld>
      <Fld label="Raça/Cor">
        <Sel value={state.raca_cor} onValueChange={v => setState({ ...state, raca_cor: v })} categoria="RACA_COR" />
      </Fld>
      <Fld label="Grau de Instrução">
        <Sel value={state.grau_instr} onValueChange={v => setState({ ...state, grau_instr: v })} categoria="GRAU_INSTRUCAO" />
      </Fld>
      <Fld label="Nome Social (opcional)" full>
        <Input value={state.nm_soc || ''} onChange={e => setState({ ...state, nm_soc: e.target.value })} />
      </Fld>
    </>
  );
}

// ============================================================================
// S-2206 — Alteração Contratual
// ============================================================================
export function S2206Form({ state, setState }) {
  return (
    <>
      <EmpresaFuncionarioSelect
        companyId={state.company_id} employeeId={state.employee_id}
        onChange={p => setState({ ...state, ...p })}
      />
      <Fld label="CPF do Trabalhador *">
        <Input value={state.cpf_trab || ''} onChange={e => setState({ ...state, cpf_trab: e.target.value.replace(/\D/g, '') })} />
      </Fld>
      <Fld label="Matrícula *">
        <Input value={state.matricula || ''} onChange={e => setState({ ...state, matricula: e.target.value })} />
      </Fld>
      <Fld label="Data da Alteração *">
        <Input type="date" value={state.dt_alteracao || ''}
               onChange={e => setState({ ...state, dt_alteracao: e.target.value })} />
      </Fld>
      <Fld label="Novo Salário (R$) *">
        <Input type="number" step="0.01" min="0" value={state.vr_sal_fx || ''} onChange={e => setState({ ...state, vr_sal_fx: e.target.value })}
               placeholder="3000.00" />
      </Fld>
      <Fld label="Novo Cargo *">
        <Input value={state.nm_cargo || ''} onChange={e => setState({ ...state, nm_cargo: e.target.value })} />
      </Fld>
      <Fld label="CBO do Cargo *" hint="6 dígitos">
        <Input value={state.cbo_cargo || ''} onChange={e => setState({ ...state, cbo_cargo: e.target.value })}
               placeholder="252210" />
      </Fld>
      <Fld label="Categoria do Trabalhador">
        <Sel value={state.cod_categ} onValueChange={v => setState({ ...state, cod_categ: v })}
             categoria="CATEGORIAS_TRABALHADOR" />
      </Fld>
      <Fld label="Unidade Salarial">
        <Sel value={state.und_sal_fixo} onValueChange={v => setState({ ...state, und_sal_fixo: v })}
             categoria="UNIDADE_SALARIAL" />
      </Fld>
    </>
  );
}

// ============================================================================
// S-2230 — Afastamento Temporário
// ============================================================================
export function S2230Form({ state, setState }) {
  const { items: motivos } = useCodigos('MOTIVOS_AFASTAMENTO');

  const handleMotivoChange = (codigo) => {
    setState(prev => {
      const motivo = motivos.find(m => m.codigo === codigo);
      const hoje = new Date().toISOString().slice(0, 10);
      const inicio = prev.dt_ini_afast || hoje;
      let fim = prev.dt_fim_afast;
      if (motivo?.dias_padrao && inicio) {
        const d = new Date(inicio);
        d.setDate(d.getDate() + motivo.dias_padrao);
        fim = d.toISOString().slice(0, 10);
      }
      return { ...prev, cod_mot_afast: codigo, dt_ini_afast: inicio, dt_fim_afast: fim };
    });
  };

  const handleInicioChange = (e) => {
    const novaData = e.target.value;
    setState(prev => {
      const motivo = motivos.find(m => m.codigo === prev.cod_mot_afast);
      let fim = prev.dt_fim_afast;
      if (motivo?.dias_padrao && novaData) {
        const d = new Date(novaData);
        d.setDate(d.getDate() + motivo.dias_padrao);
        fim = d.toISOString().slice(0, 10);
      }
      return { ...prev, dt_ini_afast: novaData, dt_fim_afast: fim };
    });
  };

  return (
    <>
      <EmpresaFuncionarioSelect
        companyId={state.company_id} employeeId={state.employee_id}
        regime="CLT"
        onChange={p => setState({ ...state, ...p })}
      />
      <Fld label="CPF do Trabalhador *">
        <Input value={state.cpf_trab || ''} onChange={e => setState({ ...state, cpf_trab: e.target.value.replace(/\D/g, '') })} />
      </Fld>
      <Fld label="Matrícula *">
        <Input value={state.matricula || ''} onChange={e => setState({ ...state, matricula: e.target.value })} />
      </Fld>
      <Fld label="Motivo do Afastamento *" full>
        <Sel value={state.cod_mot_afast} onValueChange={handleMotivoChange}
             categoria="MOTIVOS_AFASTAMENTO" testid="esocial-s2230-mot" />
      </Fld>
      <Fld label="Início do Afastamento *">
        <Input type="date" value={state.dt_ini_afast || ''}
               onChange={handleInicioChange} />
      </Fld>
      <Fld label="Fim do Afastamento (opcional)" hint="Deixar vazio se ainda em afastamento">
        <Input type="date" value={state.dt_fim_afast || ''}
               onChange={e => setState({ ...state, dt_fim_afast: e.target.value })} />
      </Fld>
      <Fld label="Observação (opcional)" full>
        <Textarea value={state.observacao || ''} onChange={e => setState({ ...state, observacao: e.target.value })}
                  rows={2} />
      </Fld>
    </>
  );
}

// ============================================================================
// S-2298 — Reintegração
// ============================================================================
export function S2298Form({ state, setState }) {
  return (
    <>
      <EmpresaFuncionarioSelect
        companyId={state.company_id} employeeId={state.employee_id}
        onChange={p => setState({ ...state, ...p })}
      />
      <Fld label="CPF do Trabalhador *">
        <Input value={state.cpf_trab || ''} onChange={e => setState({ ...state, cpf_trab: e.target.value.replace(/\D/g, '') })} />
      </Fld>
      <Fld label="Matrícula *">
        <Input value={state.matricula || ''} onChange={e => setState({ ...state, matricula: e.target.value })} />
      </Fld>
      <Fld label="Tipo de Reintegração *" full>
        <Sel value={state.tp_reint} onValueChange={v => setState({ ...state, tp_reint: v })}
             categoria="TIPOS_REINTEGRACAO" />
      </Fld>
      <Fld label="Data do Retorno Efetivo *">
        <Input type="date" value={state.dt_efet_retorno || ''}
               onChange={e => setState({ ...state, dt_efet_retorno: e.target.value })} />
      </Fld>
      <Fld label="Data de Efeito da Reintegração *">
        <Input type="date" value={state.dt_efeito_reint || ''}
               onChange={e => setState({ ...state, dt_efeito_reint: e.target.value })} />
      </Fld>
      <Fld label="Nº Processo Judicial (se aplicável)">
        <Input value={state.nr_proc_jud || ''} onChange={e => setState({ ...state, nr_proc_jud: e.target.value })} />
      </Fld>
      <Fld label="Nº Lei Anistia (se aplicável)">
        <Input value={state.nr_lei_anistia || ''} onChange={e => setState({ ...state, nr_lei_anistia: e.target.value })} />
      </Fld>
      <Fld label="Pagamento Retroativo">
        <Sel value={state.ind_pag_retroativo} onValueChange={v => setState({ ...state, ind_pag_retroativo: v })}
             items={[{ codigo: 'S', descricao: 'Sim' }, { codigo: 'N', descricao: 'Não' }]} />
      </Fld>
    </>
  );
}

// ============================================================================
// S-2400 — Cadastro de Beneficiário RPPS
// ============================================================================
export function S2400Form({ state, setState }) {
  return (
    <>
      <EmpresaFuncionarioSelect
        companyId={state.company_id} employeeId={state.employee_id}
        hideFuncionario
        onChange={p => setState({ ...state, ...p })}
      />
      <Fld label="CPF do Beneficiário *">
        <Input value={state.cpf_benef || ''} onChange={e => setState({ ...state, cpf_benef: e.target.value })}
               placeholder="12345678900" />
      </Fld>
      <Fld label="Nº do Benefício *">
        <Input value={state.nr_beneficio || ''}
               onChange={e => setState({ ...state, nr_beneficio: e.target.value })} />
      </Fld>
      <Fld label="Início do Benefício *">
        <Input type="date" value={state.dt_ini_beneficio || ''}
               onChange={e => setState({ ...state, dt_ini_beneficio: e.target.value })} />
      </Fld>
      <Fld label="Tipo de Benefício *">
        <Sel value={state.tp_beneficio} onValueChange={v => setState({ ...state, tp_beneficio: v })}
             categoria="TIPOS_BENEFICIO_RPPS" />
      </Fld>
      <Fld label="Cadastro Inicial?">
        <Sel value={state.cad_ini} onValueChange={v => setState({ ...state, cad_ini: v })}
             items={[{ codigo: 'S', descricao: 'Sim (primeiro envio)' }, { codigo: 'N', descricao: 'Não (regularização)' }]} />
      </Fld>
      <Fld label="Tipo Plano RP">
        <Sel value={state.tp_plan_rp} onValueChange={v => setState({ ...state, tp_plan_rp: v })}
             items={[{ codigo: '1', descricao: 'Contributivo' }, { codigo: '2', descricao: 'Não-contributivo' }]} />
      </Fld>
    </>
  );
}

// ============================================================================
// S-3000 — Exclusão de evento já enviado
// ============================================================================
export function S3000Form({ state, setState }) {
  return (
    <>
      <EmpresaFuncionarioSelect
        companyId={state.company_id} employeeId={state.employee_id}
        hideFuncionario
        onChange={p => setState({ ...state, ...p })}
      />
      <Fld label="Tipo do Evento a Excluir *">
        <Sel value={state.tp_evento_excluir} onValueChange={v => setState({ ...state, tp_evento_excluir: v })}
             categoria="TIPOS_EXCLUIVEIS" />
      </Fld>
      <Fld label="Nº do Recibo do Evento *" hint="Recibo recebido na transmissão original">
        <Input value={state.nr_recibo_excluir || ''}
               onChange={e => setState({ ...state, nr_recibo_excluir: e.target.value })} />
      </Fld>
      <Fld label="CPF do Trabalhador" hint="Obrigatório p/ S-2xxx" full>
        <Input value={state.cpf_trabalhador || ''}
               onChange={e => setState({ ...state, cpf_trabalhador: e.target.value })} />
      </Fld>
    </>
  );
}

// ============================================================================
// S-1000 — Cadastro do Empregador
// ============================================================================
export function S1000Form({ state, setState }) {
  return (
    <>
      <EmpresaFuncionarioSelect
        companyId={state.company_id} employeeId=""
        hideFuncionario
        onChange={p => setState({ ...state, ...p })}
      />
      <Fld label="Classificação Tributária *">
        <Sel value={state.classe_tributaria} onValueChange={v => setState({ ...state, classe_tributaria: v })}
             categoria="CLASSIFICACAO_TRIBUTARIA" />
      </Fld>
      <Fld label="Início da Validade (YYYY-MM) *">
        <Input value={state.ini_validade || ''}
               onChange={e => setState({ ...state, ini_validade: e.target.value })}
               placeholder="2026-01" />
      </Fld>
      <Fld label="Nome do Contato *">
        <Input value={state.contato_nome || ''}
               onChange={e => setState({ ...state, contato_nome: e.target.value })} />
      </Fld>
      <Fld label="CPF do Contato *">
        <Input value={state.contato_cpf || ''}
               onChange={e => setState({ ...state, contato_cpf: e.target.value })} />
      </Fld>
      <Fld label="Telefone do Contato *">
        <Input value={state.contato_fone || ''}
               onChange={e => setState({ ...state, contato_fone: e.target.value })}
               placeholder="11999998888" />
      </Fld>
      <Fld label="Email do Contato *">
        <Input value={state.contato_email || ''}
               onChange={e => setState({ ...state, contato_email: e.target.value })} />
      </Fld>
    </>
  );
}

// ============================================================================
// S-1005 — Estabelecimento
// ============================================================================
export function S1005Form({ state, setState }) {
  return (
    <>
      <EmpresaFuncionarioSelect
        companyId={state.company_id} employeeId=""
        hideFuncionario
        onChange={p => setState({ ...state, ...p })}
      />
      <Fld label="CNPJ do Estabelecimento (opcional)" hint="Vazio = matriz">
        <Input value={state.cnpj_estabelecimento || ''}
               onChange={e => setState({ ...state, cnpj_estabelecimento: e.target.value })} />
      </Fld>
      <Fld label="CNAE Preponderante *" hint="7 dígitos">
        <Input value={state.cnae_preponderante || ''}
               onChange={e => setState({ ...state, cnae_preponderante: e.target.value })}
               placeholder="6201500" />
      </Fld>
      <Fld label="Alíquota RAT (opcional)" hint="1, 2 ou 3 — só se houver processo">
        <Input value={state.aliq_rat || ''}
               onChange={e => setState({ ...state, aliq_rat: e.target.value })} />
      </Fld>
      <Fld label="FAP (opcional)" hint="Ex.: 1.0000 — só se houver processo">
        <Input value={state.fap || ''}
               onChange={e => setState({ ...state, fap: e.target.value })} />
      </Fld>
      <Fld label="Início da Validade (YYYY-MM) *" full>
        <Input value={state.ini_validade || ''}
               onChange={e => setState({ ...state, ini_validade: e.target.value })}
               placeholder="2026-01" />
      </Fld>
    </>
  );
}

// ============================================================================
// S-1010 — Rubrica
// ============================================================================
export function S1010Form({ state, setState }) {
  return (
    <>
      <EmpresaFuncionarioSelect
        companyId={state.company_id} employeeId=""
        hideFuncionario
        onChange={p => setState({ ...state, ...p })}
      />
      <Fld label="Código da Rubrica *">
        <Input value={state.cod_rubrica || ''}
               onChange={e => setState({ ...state, cod_rubrica: e.target.value })} />
      </Fld>
      <Fld label="Tipo da Rubrica *">
        <Sel value={state.tipo_rubrica} onValueChange={v => setState({ ...state, tipo_rubrica: v })}
             categoria="TIPOS_RUBRICA" />
      </Fld>
      <Fld label="Descrição *" full>
        <Input value={state.descricao_rubrica || ''}
               onChange={e => setState({ ...state, descricao_rubrica: e.target.value })} />
      </Fld>
      <Fld label="Natureza da Rubrica *" full>
        <Sel value={state.natureza_rubrica} onValueChange={v => setState({ ...state, natureza_rubrica: v })}
             categoria="NATUREZA_RUBRICA" />
      </Fld>
      <Fld label="Início da Validade (YYYY-MM) *">
        <Input value={state.ini_validade || ''}
               onChange={e => setState({ ...state, ini_validade: e.target.value })}
               placeholder="2026-01" />
      </Fld>
    </>
  );
}

// ============================================================================
// S-1020 — Lotação Tributária
// ============================================================================
export function S1020Form({ state, setState }) {
  return (
    <>
      <EmpresaFuncionarioSelect
        companyId={state.company_id} employeeId=""
        hideFuncionario
        onChange={p => setState({ ...state, ...p })}
      />
      <Fld label="Código da Lotação *">
        <Input value={state.cod_lotacao || ''}
               onChange={e => setState({ ...state, cod_lotacao: e.target.value })} />
      </Fld>
      <Fld label="Tipo de Lotação *">
        <Sel value={state.tp_lotacao} onValueChange={v => setState({ ...state, tp_lotacao: v })}
             categoria="TIPOS_LOTACAO" />
      </Fld>
      <Fld label="CNPJ da Lotação (opcional)">
        <Input value={state.cnpj_lotacao || ''}
               onChange={e => setState({ ...state, cnpj_lotacao: e.target.value })} />
      </Fld>
      <Fld label="FPAS *" hint="515 comércio · 612 indústria · 833 outros">
        <Input value={state.fpas || ''}
               onChange={e => setState({ ...state, fpas: e.target.value })} placeholder="515" />
      </Fld>
      <Fld label="Cód. Terceiros *" hint="0000 = Simples Nacional">
        <Input value={state.cod_tercs || ''}
               onChange={e => setState({ ...state, cod_tercs: e.target.value })} placeholder="0000" />
      </Fld>
      <Fld label="Início da Validade (YYYY-MM) *">
        <Input value={state.ini_validade || ''}
               onChange={e => setState({ ...state, ini_validade: e.target.value })}
               placeholder="2026-01" />
      </Fld>
    </>
  );
}

// ============================================================================
// S-1070 — Tabela de Processos Administrativos/Judiciais
// ============================================================================
export function S1070Form({ state, setState }) {
  return (
    <>
      <EmpresaFuncionarioSelect
        companyId={state.company_id} employeeId=""
        hideFuncionario
        onChange={p => setState({ ...state, ...p })}
      />
      <Fld label="Tipo do Processo *">
        <Sel value={state.tp_proc} onValueChange={v => setState({ ...state, tp_proc: v })}
             categoria="TIPOS_PROCESSO" />
      </Fld>
      <Fld label="Indicativo de Matéria *">
        <Sel value={state.ind_mat_proc} onValueChange={v => setState({ ...state, ind_mat_proc: v })}
             categoria="INDICATIVO_MATERIA" />
      </Fld>
      <Fld label="Nº do Processo *" full>
        <Input value={state.nr_proc || ''}
               onChange={e => setState({ ...state, nr_proc: e.target.value })}
               placeholder="0000000-00.0000.0.00.0000" />
      </Fld>
      <Fld label="Início da Validade (YYYY-MM) *">
        <Input value={state.ini_validade || ''}
               onChange={e => setState({ ...state, ini_validade: e.target.value })}
               placeholder="2026-01" />
      </Fld>
      <Fld label="Fim da Validade (YYYY-MM, opcional)">
        <Input value={state.fim_validade || ''}
               onChange={e => setState({ ...state, fim_validade: e.target.value })} />
      </Fld>
      <Fld label="Observação (opcional)" full>
        <Textarea value={state.observacao || ''}
                  onChange={e => setState({ ...state, observacao: e.target.value })} rows={2} />
      </Fld>
    </>
  );
}

// MOTIVOS_DESLIGAMENTO exposto pra reuso por outras telas (Rescisões)
export { MOTIVOS_DESLIGAMENTO };
